import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ModalContent from "../../../common/components/Modal";
import { useTranslation } from "react-i18next";
import { AssetApi } from "../../../services/netServices";
import { GlobalErrorBox } from "../../../common/components/ErrorBox";

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ImportModal({
  open,
  onClose,
  onSuccess,
}: ImportModalProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const { t } = useTranslation() as any;

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setErrors([]);
      setTabIndex(0);
    }
  }, [open]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setSelectedFile(null);
    setErrors([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setErrors([]);
    }
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setErrors([]);
      const dataLocalStorage = localStorage.getItem("USER");
      const parsedData = JSON.parse(dataLocalStorage ?? "");
      const token = parsedData?.User?.token;
      await new AssetApi().assetImportPost(token, selectedFile);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.log(err);
      setErrors([err?.response?.data?.Message || "Import thất bại"]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box>
        <ModalContent onClose={onClose} title={t("00_03_import")} width={600}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="import tabs"
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label={t("34_00_import_excel")} />
            <Tab label={t("34_00_import_zip")} />
          </Tabs>

          <GlobalErrorBox errors={errors} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 3,
            }}
          >
            <Typography sx={{ mb: 2 }}>
              {tabIndex === 0
                ? t("34_00_import_excel_dsc")
                : t("34_00_import_zip_dsc")}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{ textTransform: "none" }}
            >
              {tabIndex === 0 ? t("34_00_import_excel") : t("34_00_import_zip")}
              <input
                type="file"
                hidden
                accept={tabIndex === 0 ? ".xlsx,.xls" : ".zip"}
                onChange={handleFileChange}
              />
            </Button>

            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: "gray" }}>
                {selectedFile.name}
              </Typography>
            )}
          </Box>

          {uploading && <LinearProgress sx={{ mb: 3 }} />}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              style={{ borderColor: "#e5e5e5", color: "#000" }}
              onClick={onClose}
            >
              {t("00_03_cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={!selectedFile || uploading}
              onClick={handleUpload}
            >
              {t("00_03_import")}
            </Button>
          </Box>
        </ModalContent>
      </Box>
    </Modal>
  );
}
